/* eslint-disable max-lines */

import { selectReviewSet } from 'actions';
import Permissions from 'components/Permissions';
import Tooltip from 'components/Tooltip';
import { ARROW_CLOSE_ENVELOPE, WARNING_ICON_REDESIGN } from 'constants/envelopeIcons';
import { concatFromQueryParam } from 'constants/eventsUtils';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import moment from 'moment';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getCustomerDomain, getUser } from 'selectors/auth';
import { getSelectedReviewSet } from 'selectors/savedSearches';
import type { RouteParams, SavedSearch } from 'types';
import { getParamsFromUrl, useHistory } from 'utils/urls';

type ComponentProps = {
  reviewSet: SavedSearch;
  openReviewSet?: string;
  handleOpenReviewSet?: (reviewSetId: string) => void;
};

const ReviewSetListItem: React.FC<ComponentProps> = ({
  reviewSet,
  openReviewSet,
  handleOpenReviewSet,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const user = useSelector(getUser);
  const selectedReviewSet = useSelector(getSelectedReviewSet);
  const customerDomain = useSelector(getCustomerDomain);

  const { children } = reviewSet;
  const isOpen = reviewSet.uuid === openReviewSet;

  const qp = new URLSearchParams(location.search);

  const queryParams = concatFromQueryParam(
    qp.toString(),
    location.pathname,
    resourceQueryParamName.savedSearch
  );

  const handleSelectReviewSet = (): void => {
    history.pushLookup({
      customerDomain,
      routeName: 'review-set-manager-selected',
      routeParams: { reviewSetId: reviewSet.uuid },
      queryParams,
    });
    dispatch(selectReviewSet({ reviewSet }));
  };

  const getDigest = (): string => {
    if (reviewSet.custom_reports?.length) {
      const kinds = new Set([
        ...reviewSet.custom_reports
          .filter((report) => report.deleted_at === null)
          .map((report) => `${report.kind.charAt(0).toUpperCase()}${report.kind.slice(1)}`),
      ]);
      return [...kinds].filter((k) => k.trim() !== '').join(' & ');
    }
    return '-';
  };

  let navParams: RouteParams = {};

  if (user.customer) {
    if (reviewSet) {
      navParams = getParamsFromUrl(
        reviewSet.url,
        resourceQueryParamName.envelopes,
        'envelope-list',
        user.customer?.config
      );
    }
  }

  const whitelistParams = [
    'filters_search',
    'has_events',
    'limit',
    'matches',
    'offset',
    'order_by',
    'order_desc',
    'review_values',
  ];

  const renderV1Warning = Object.keys(navParams).some((k) => !whitelistParams.includes(k));

  const getBackgroundColor = (): string => {
    if (selectedReviewSet && reviewSet.uuid === selectedReviewSet.uuid) {
      return 'bg-litlingo-focus-area-background';
    }
    if (reviewSet.parent_uuid) {
      return 'bg-litlingo-gray-0.5 hover:bg-litlingo-gray-0.5';
    }

    return 'bg-white hover:bg-litlingo-gray-0.5';
  };

  return (
    <>
      <tr
        className={`table-row border-t border-gray-200 align-middle ${getBackgroundColor()}`}
        role="button"
        aria-hidden
        data-testid="review-set"
        onClick={handleSelectReviewSet}
      >
        <td className="table-wrapper__new-td pl-0">
          <div className="h-9 m-0 pl-0 p-0 flex flex-row justify-center items-center">
            {children && children?.length > 0 && (
              <button
                type="button"
                className="h-9 w-full flex flex-row justify-center items-center focus:outline-none"
                onClick={(e): void => {
                  e.stopPropagation();
                  if (handleOpenReviewSet) {
                    handleOpenReviewSet(reviewSet.uuid);
                  }
                }}
              >
                <span
                  className={`w-4 focus:outline-none transition-transform duration-300 ${
                    isOpen && 'transform rotate-90'
                  }`}
                >
                  {ARROW_CLOSE_ENVELOPE()}
                </span>
              </button>
            )}
          </div>
        </td>
        <td className="table-wrapper__new-td">
          <div className="table-wrapper__new-cell-content h-9 m-0 pl-0 text-body truncate flex flex-row items-center">
            <span>{reviewSet.name}</span>
          </div>
        </td>
        <Permissions action="reviewSets.needToUpdateWarning">
          <td className="table-wrapper__new-td pl-4">
            {renderV1Warning && (
              <Tooltip content="Need to update query" redesign>
                <span>{WARNING_ICON_REDESIGN()}</span>
              </Tooltip>
            )}
          </td>
        </Permissions>
        <td className="table-wrapper__new-td">
          <div className="table-wrapper__new-cell-content h-auto flex text-body">
            {reviewSet.priority}
          </div>
        </td>

        <td className="table-wrapper__new-td">
          <div className="table-wrapper__new-cell-content h-auto flex text-body">
            {reviewSet.record_count?.toLocaleString()}
          </div>
        </td>

        <td className="table-wrapper__new-td">
          <div className="table-wrapper__new-cell-content h-auto flex text-body">
            {moment(reviewSet.updated_at).format('MM/DD/YYYY')}
          </div>
        </td>
        <td className="table-wrapper__new-td">
          <div className="table-wrapper__new-cell-content h-auto text-body text-left pl-2">
            {getDigest()}
          </div>
        </td>
      </tr>
      {isOpen &&
        children &&
        children.map((child) => <ReviewSetListItem key={child.uuid} reviewSet={child} />)}
    </>
  );
};

export default ReviewSetListItem;
